import { useCallback, useEffect, memo, useState } from "react";
import type { TopNavbarProps } from "../../../smb-ui-welcome/src/types/components/core.type";
import { LanguageSwitch } from "./language-switch";
import SupportMenu from "@repo/ui/components/shared/supportmenu";
import { HelpQuestionIcon } from "@repo/ui/components/shared/SMBIcons";
import { useIsMobile } from "@repo/ui/hooks/use-mobile";
import { useIsTab } from "@repo/ui/hooks/useIsTab";
import MobileNavbar from "./MobileNavbar";
import USBModal, { ModalHeader } from "@usb-shield/react-modal";

import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@repo/ui/components/ui/popover";

import { SUPPORT_MENU_LABEL } from "#constants/index.ts";

const { VITE_ELAVON_LOGIN_URL, VITE_AEM_BASE_URL, ENABLE_LANGUAGE_SWITCHER = false } = window.__conf__;



const TopNavbar = memo(({
  aemContent = {
    primaryLogoImage: "",
    primaryLogoImageAltText: "",
    primaryButton: { label: "", displayIcon: false },
    support: {
      label: "",
      supportHeader: "",
      supportHeaderBody: "",
      cobrowseHeader: "",
      cobrowseText: "",
    },
    navs: {
      language: "",
      mainMenu: "",
    }
  },
  language,
  setLanguage,
}: TopNavbarProps) => {
  const [showSupportMenuPopover, setShowSupportMenuPopover] =
    useState<boolean>(false);
  const [showMobileSupportModal, setShowMobileSupportModal] =
    useState<boolean>(false);
  const { primaryLogoImage, primaryLogoImageAltText, primaryButton, support, navs } = aemContent;

  const redirectTo = useCallback((url: string | URL | undefined) => {
    window.open(url, '_self');
  }, []);


  const onSupportClick = () => {
    setShowSupportMenuPopover(!showSupportMenuPopover);
  }


  const isMobile = useIsMobile();
  const isTab = useIsTab();

  useEffect(() => {
    if (!showMobileSupportModal) {
      return;
    }

    document
      .querySelectorAll("#support-mobile-modal .usb-modal-v2--dialog")
      ?.forEach((modal) => {
        modal.setAttribute("aria-label", "Support");
        modal.removeAttribute("aria-labelledby");
      });
  }, [showMobileSupportModal]);

  return (
    <header
      data-testid="top-navbar"
      className="w-full flex items-center justify-between h-16 px-4 lg:px-[6.25%]"
    >
      {(ENABLE_LANGUAGE_SWITCHER && (isMobile || isTab)) && (
        <MobileNavbar
          onSupportClick={() => setShowMobileSupportModal(true)}
          onLoginClick={() => redirectTo(VITE_ELAVON_LOGIN_URL)}
          setLanguage={setLanguage}
          language={language}
          aemData={{ primaryLogoImage, primaryLogoImageAltText, primaryButton, support, navs }}
        />
      )}
      <picture>
        <img
          src={`${VITE_AEM_BASE_URL}${primaryLogoImage}`}
          alt={primaryLogoImageAltText}
          loading="lazy"
        />
      </picture>
      <div className="flex items-center gap-6">
        <div className="hidden lg:flex">
          {ENABLE_LANGUAGE_SWITCHER && <LanguageSwitch language={language} setLanguage={setLanguage} />}
        </div>
        <div className="hidden lg:flex">
          {ENABLE_LANGUAGE_SWITCHER && <Popover
            open={showSupportMenuPopover}
            defaultOpen={true}
            onOpenChange={(open) => {
              if (open) {
                onSupportClick();
              }
              setShowSupportMenuPopover(open);
            }}
          >
            <PopoverTrigger>
              <div
                role="button"
                key="support"
                className="px-0 font-normal flex items-center gap-1"
                data-testid="btn-component-support"
                aria-label="Support"
                style={{
                  color: "#2E2E32",
                }}
              >
                <span className="flex shrink-0 items-center">
                  <HelpQuestionIcon />
                </span>
                <span
                  className="focus-visible hidden md:flex !text-[#2E2E32] font-[400]"
                  style={{ fontSize: "14px" }}
                >
                  {SUPPORT_MENU_LABEL}
                </span>
              </div>
            </PopoverTrigger>
            <PopoverContent
              aria-label="Support"
              className="border-0 p-0"
              side="bottom"
              align="end"
              style={{
                boxShadow: "0px 5px 15px rgba(0, 0, 0, 0.35)",
                width: "296px",
                borderRadius: "8px",
                overflow: "hidden",
              }}
            >
              <SupportMenu locale={language} aemData={{ support }} />
            </PopoverContent>
          </Popover>}
        </div>
        {showMobileSupportModal && (
          <USBModal
            id="support-mobile-modal"
            isOpen={showMobileSupportModal}
            handleClose={() => setShowMobileSupportModal(false)}
            className="[&_.usb-modal-v2--header]:sr-only"
            aria-label="Support"
          >
            <ModalHeader id="support-mobile-modal-header">Support</ModalHeader>
            <SupportMenu locale={language} aemData={{ support }} />
          </USBModal>
        )}
      </div>
    </header>
  );
});

export default TopNavbar;
